import { fetchDashboardData } from './dataClient';

// Business information types
export interface Business {
  id: string;
  name: string;
  type: string;
  address: string;
  size: number;
  yearBuilt: number;
  operationalHours: {
    start: string;
    end: string;
    daysOpen: number[];
  };
  renewablePercentage: number;
  planType: string;
  contactName: string;
  contactEmail: string;
  zonesCount: number;
  badges: string[];
  joinDate: string;
}

// Building score types
export interface BuildingScore {
  overallScore: number;
  energyEfficiency: number;
  sustainabilityPerformance: number;
  historicalScores: {
    month: string;
    score: number;
  }[];
}

// Energy usage types
export interface MonthlyUsage {
  dailyData: any;
  month: string;
  kWh: number;
  cost: number;
}

export interface ZoneUsage {
  zone: string;
  percentage: number;
  kWh: number;
}

export interface HourlyUsage {
  hour: string;
  kWh: number;
  operational: boolean;
}

export interface PeakDemand {
  day: string;
  time: string;
  kW: number;
}

export interface Anomaly {
  date: string;
  percentage: number;
  reason: string;
}

export interface ExpensiveUseTime {
  time: string;
  rate: string;
  cost: number;
}

export interface BillForecast {
  currentBill: number;
  forecastNextMonth: number;
  bestCase: number;
  worstCase: number;
}

export interface OperationalUsage {
  operational: number;
  nonOperational: number;
}

export interface EnergyUsageData {
  monthly: MonthlyUsage[];
  submetering: ZoneUsage[];
  hourly: HourlyUsage[];
  peakDemandTimes: PeakDemand[];
  anomalies: Anomaly[];
  expensiveUseTimes: ExpensiveUseTime[];
  billForecast?: BillForecast;
  operationalVsNonOperational: OperationalUsage;
}

// Sector comparison types
export interface SectorStats {
  averageScore: number;
  averageEnergyUsage: number;
  averageRenewable: number;
  topPerformer: {
    name: string;
    id: string;
    score: number;
  };
  businesses: {
    name: string;
    id: string;
    score: number;
  }[];
}

// Badge types
export interface Badge {
  id?: string;
  title: string;
  description: string;
  icon: string;
  criteria: string;
  shareText: string;
  color: string;
}

// Carbon impact types
export interface CarbonImpactData {
  totalCO2Avoided: number;
  treesEquivalent: number;
  carMilesEquivalent: number;
  coalSaved: number;
  smartphonesCharged: number;
  yearlyData: {
    month: string;
    co2Avoided: number;
    yearMonth: string;
  }[];
  period?: {
    start: string;
    end: string;
  };
}

// Energy overview types
export interface EnergyOverviewData {
  lastMonthKwh: number;
  lastMonthName: string;
  thisMonthSoFarKwh: number;
  thisMonthName: string;
  monthlyAvgKwh: number;
  peakUsageTimes: {
    date: string;
    formattedDate: string;
    day: string;
    time: string;
    kwh: number;
  }[];
  periodCovered: {
    start: string;
    end: string;
  };
}

// Energy insights types
export interface TimeSeriesDataPoint {
  timestamp: string;
  kwh: number;
}

export interface YearlyDataPoint {
  month: string;
  year: number;
  kwh: number;
}

// Legacy types for backward compatibility
export interface DailyUsageData {
  time: string;
  kwh: number;
  hour: number;
  isOperational: boolean;
}

export interface WeeklyUsageData {
  time: string;
  day: string;
  hour: number;
  kwh: number;
  isOperational: boolean;
}

export interface MonthlyUsageData {
  week: string;
  weekNumber: number;
  kwh: number;
  isOperational: boolean;
}

export interface YearlyUsageData {
  month: string;
  monthNumber: number;
  kwh: number;
  previousYear: number;
  isOperational: boolean;
}

export interface PeakTimeData {
  date: string;
  formattedDate: string;
  day: string;
  time: string;
  kwh: number;
}

export interface OperationalUsageData {
  month: string;
  operational: number;
  nonOperational: number;
}

export interface HourlyAvgData {
  hour: number;
  time: string;
  kwh: number;
  isOperational: boolean;
}

export interface PeriodCoveredData {
  start: string;
  end: string;
  latestDay: string;
  latestWeek: {
    start: string;
    end: string;
  };
  latestMonth: string;
  latestYear: string;
}

export interface EnergyInsightsData {
  // New data format
  daily_latest: TimeSeriesDataPoint[];
  weekly_latest_hourly: TimeSeriesDataPoint[];
  monthly_latest_daily: TimeSeriesDataPoint[];
  yearly_latest_monthly: {
    current_year: YearlyDataPoint[];
    previous_year: YearlyDataPoint[];
  };
  hourly_last_7_days: TimeSeriesDataPoint[];
  all_time_monthly: YearlyDataPoint[];
  
  // Additional keys used by UsagePatterns component
  last_week_hourly?: TimeSeriesDataPoint[];
  last_week_daily?: TimeSeriesDataPoint[];
  last_30_days_hourly?: TimeSeriesDataPoint[];
  last_30_days_daily?: TimeSeriesDataPoint[];
  last_30_days_weekly?: TimeSeriesDataPoint[];
  last_year_weekly?: TimeSeriesDataPoint[];
  last_year_monthly?: {
    current_year: YearlyDataPoint[];
    previous_year: YearlyDataPoint[];
  };
  hourly_last_30_days?: TimeSeriesDataPoint[];
  
  // Legacy format for backward compatibility
  usagePatterns?: {
    daily: DailyUsageData[];
    weekly: WeeklyUsageData[];
    monthly: MonthlyUsageData[];
    yearly: YearlyUsageData[];
  };
  
  peakTimesLatestMonth: PeakTimeData[];
  operationalUsage: OperationalUsageData;
  dayTypePatterns: {
    weekday: HourlyAvgData[];
    weekend: HourlyAvgData[];
  };
  periodCovered: PeriodCoveredData;
}

export interface UsageForecastData {
  forecastSeries: {
    month: string;
    monthName: string;
    actualKwh: number | null;
    forecastKwh: number | null;
  }[];
  forecast_1_month: {
    actual: {
      date: string;
      kwh: number;
    }[];
    forecast: {
      date: string;
      kwh: number;
    }[];
  };
  forecast_3_months: {
    actual: {
      date: string;
      kwh: number;
    }[];
    forecast: {
      date: string;
      kwh: number;
    }[];
  };
  forecast_1_year: {
    actual: {
      date: string;
      kwh: number;
    }[];
    forecast: {
      date: string;
      kwh: number;
    }[];
  };
  weekly: {
    period: string;
    forecastKwh: number;
  };
  monthly: {
    period: string;
    forecastKwh: number;
  };
  yearly: {
    period: string;
    forecastKwh: number;
  };
  currentMonth: {
    month: string;
    name: string;
    actualKwh: number;
    forecastKwh: number | null;
  };
  periodCovered: {
    start: string;
    end: string;
  };
}

// Peak demand types
export interface PeakDemandData {
  last_30_days_top_peaks: {
    date: string;
    formatted_date: string;
    hour: number;
    formatted_hour: string;
    kwh: number;
  }[];
  monthly_analysis: {
    [yearMonth: string]: {
      year: number;
      month: number;
      month_name: string;
      formatted_month: string;
      top_10_absolute_peaks: {
        date: string;
        formatted_date: string;
        hour: number;
        formatted_hour: string;
        kwh: number;
      }[];
      average_peak_hour_overall: {
        hour: number;
        formatted_hour: string;
        avg_kwh: number;
      };
      average_peak_hour_by_weekday: {
        [day: string]: {
          hour: number | null;
          formatted_hour: string;
          avg_kwh: number;
        };
      };
    };
  };
  period_covered: {
    start: string;
    end: string;
    last_30_days_start: string;
  };
}

// Function implementations
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const setToken = (businessId: string) => {
  localStorage.setItem('businessId', businessId);
};

export const getBusinessId = (): string | null => {
  return localStorage.getItem('businessId');
};

export const clearToken = () => {
  localStorage.removeItem('businessId');
};

export const isAuthenticated = (): boolean => {
  return !!getBusinessId();
};

export const login = async (businessId: string): Promise<string> => {
  // Only allow BIZ001 to login
  if (businessId !== 'BIZ001') {
    throw new Error('Invalid business ID. Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  // Simulate network delay for login
  await delay(600);
  
  // Set token in localStorage
  setToken(businessId);
  
  return businessId;
};

export const getBusiness = async (businessId: string): Promise<Business> => {
  // Only return data for BIZ001
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  return fetchDashboardData<Business>(businessId, 'business_profile');
};

export const getBuildingScore = async (businessId: string): Promise<BuildingScore | null> => {
  // We're removing this feature as per instructions
  return null;
};

export const getEnergyUsage = async (businessId: string): Promise<EnergyUsageData | null> => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  // This is a beta feature, return null
  return null;
};

export const getSectorData = async (businessType: string): Promise<SectorStats | null> => {
  // We're removing this feature as per instructions
  return null;
};

export const getLeaderboard = async (category: string): Promise<any[]> => {
  // We're removing this feature as per instructions
  return [];
};

export const getBadges = async (businessId: string): Promise<Badge[]> => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  const [business, badgeDefinitions] = await Promise.all([
    getBusiness(businessId),
    getAllBadges()
  ]);
  
  return business.badges.map(badgeId => ({
    ...badgeDefinitions[badgeId],
    id: badgeId
  }));
};

export const getBadgeById = async (badgeId: string): Promise<Badge> => {
  const badgeDefinitions = await getAllBadges();
  const badge = badgeDefinitions[badgeId];
  
  if (!badge) {
    throw new Error(`Badge with ID ${badgeId} not found.`);
  }
  
  return {
    ...badge,
    id: badgeId
  };
};

export const getAllBadges = async (): Promise<Record<string, Badge>> => {
  const badgeDefinitions = await fetchDashboardData<Record<string, Badge>>('BIZ001', 'badge_definitions');
  const badgesWithIds: Record<string, Badge> = {};
  
  Object.entries(badgeDefinitions).forEach(([id, badge]) => {
    badgesWithIds[id] = {
      ...badge,
      id
    };
  });
  
  return badgesWithIds;
};

export const getCarbonImpact = async (businessId: string): Promise<CarbonImpactData> => {
  return fetchDashboardData<CarbonImpactData>(businessId, 'carbon_impact');
};

export const getEnergyOverview = async (businessId: string): Promise<EnergyOverviewData> => {
  return fetchDashboardData<EnergyOverviewData>(businessId, 'energy_overview');
};

export const getEnergyInsights = async (businessId: string): Promise<EnergyInsightsData> => {
  return fetchDashboardData<EnergyInsightsData>(businessId, 'energy_insights');
};

export const getUsageForecast = async (businessId: string): Promise<UsageForecastData> => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  return fetchDashboardData<UsageForecastData>(businessId, 'usage_forecast');
};

export const getSeasonalInsights = async (businessId: string) => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  return fetchDashboardData(businessId, 'seasonal_insights');
};

export const getEfficiencyMetrics = async (businessId: string) => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  return fetchDashboardData(businessId, 'efficiency_metrics');
};

export const getPeakDemand = async (businessId: string): Promise<PeakDemandData> => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  return fetchDashboardData<PeakDemandData>(businessId, 'peak_demand');
};
